import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppComponent } from '../../app.component';
import { AnalysisScope, describeScope, ScopeService, wholeOrganisation } from '../../analysis/scope';
import { ScopeLinqsetService } from '../../analysis/scope-linqset.service';
import { Linqset } from '../../common/linqset-model';
import { Organisation } from '../../common/organisation';
import { ONBOARDING_STEPS } from '../../common/user';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { HelpService } from '../../help/help-services';
import { analysisTour } from '../../analysis/analysis-tour';
import { OrganisationService } from '../../organisation/organisation.service';
import { UserService } from '../../user/user.service';
import { SCREEN } from '../layouts-enums';

// The Nodes, Insights, Tags, Information and AI summary views of the analysis: every node in what is looked at, by
// type, with its figures, in the same table the Insights screens have; the insights overview of it; its work by tag;
// its outputs as information assets; or a written summary of it by AI. All need the same data, so one page shows
// whichever the route asks for, with the same scope as the other views, from the header
@Component({
    selector: 'app-nodes-layout',
    templateUrl: './nodes-layout.html',
    styleUrls: ['./nodes-layout.css'],
    standalone: false,
})
export class NodesLayoutComponent implements OnInit, OnDestroy {
    readonly screen = SCREEN.ANALYSIS;
    view: 'nodes' | 'ai' | 'insights' | 'tags' | 'information' = 'nodes';
    organisationKey = '';
    organisation?: Organisation;
    linqset?: Linqset;
    comparison?: Linqset;
    organisationLinqset?: Linqset;
    currentStateOrganisationLinqset?: Linqset;    // in a project, the current state's roles and systems, to compare with
    scope: AnalysisScope = wholeOrganisation();
    description = '';
    loading = true;
    error = '';
    reloadTrigger: any = {};
    private subs: Subscription[] = [];
    private request = 0;

    constructor(private route: ActivatedRoute, private organisationService: OrganisationService, private scopeService: ScopeService, private scopeLinqset: ScopeLinqsetService,
        private functionService: FunctionService, private userService: UserService, private helpService: HelpService) {}

    ngOnInit() {
        this.subs.push(this.route.data.subscribe(d => {
            this.view = ['ai', 'insights', 'tags', 'information'].includes(d['view']) ? d['view'] : 'nodes'
            // seeing the insights is a step of getting started, ticked off on the home page's checklist
            if (this.view === 'insights') this.userService.saveOnboardingStep(ONBOARDING_STEPS.FIRST_INSIGHTS)
        }));
        this.subs.push(this.route.params.subscribe(p => {
            this.organisationKey = p['organisation_key'];
            this.scopeService.open(this.organisationKey);
        }));
        this.subs.push(this.scopeService.scope$.subscribe(scope => {
            this.scope = scope;
            void this.load();
        }));
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
        this.helpService.endTour();
    }

    // reads everything again: the organisation's nodes and insights changed on the server (output values were set)
    async reload() {
        await this.load(true);
    }

    private async load(afresh = false) {
        const request = ++this.request;
        this.loading = true;
        this.error = '';
        try {
            const organisation = await this.organisationService.checkOrganisationDataForRoute(this.organisationKey);
            if (request !== this.request) return;
            const { linqset, comparison } = await this.scopeLinqset.load(organisation, this.scope, this.userService.getUser()?._key, afresh);
            if (request !== this.request) return;
            this.organisation = organisation;
            this.linqset = linqset;
            this.comparison = comparison;
            this.organisationLinqset = this.scopeLinqset.organisationLinqset;
            this.currentStateOrganisationLinqset = this.scopeLinqset.currentStateOrganisationLinqset;
            this.description = describeScope(this.scope, organisation);
            this.reloadTrigger = {};
        } catch (err) {
            if (request !== this.request) return;
            console.error('Unable to load the nodes', err);
            this.error = "The nodes couldn't be loaded. Please try again.";
        } finally {
            if (request === this.request) this.loading = false;
        }
    }

    async handleFunctionEvent(event: { function: FUNCTION; value?: any }) {
        if (event.function === FUNCTION.SCREEN_TOUR) {
            this.helpService.showWhatsThisOverlay(analysisTour(this.view));
            return;
        }
        try {
            await this.functionService.executeFunction(event.function, event.value, undefined, undefined, undefined);
        } catch (err) {
            console.error(err);
        }
    }

    isHelpOn() {
        return AppComponent.help;
    }
}
